/**
 * Receipts and the business day.
 *
 * The distinction this module exists to preserve: an X report is a look at the
 * day so far and changes every time it is read; a Z report is the day signed
 * off and never changes again. The UI must not blur them, so they are separate
 * hooks with different caching, rather than one hook with a flag.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface TenderLine {
  tender_type: string;
  gross: string;
  refunded: string;
  net: string;
  tips: string;
  count: number;
}

export interface DrawerLine {
  drawer_id: string;
  expected: string;
  counted: string;
  variance: string;
}

export interface DayReport {
  business_date: string;
  is_final: boolean;
  /** ISO 4217, carried on the document so it is legible without a second lookup. */
  currency: string;
  gross_sales: string;
  refunds: string;
  /** What the menu sold: takings less refunds and service charge. */
  net_sales: string;
  /**
   * Added by service charge rules, less its share of refunds. Its own figure,
   * not a sale of anything on the menu. Absent on a Z signed before it was.
   */
  service_charges?: string;
  tips: string;
  total_collected: string;
  tenders: TenderLine[];
  drawers: DrawerLine[];
  drawer_variance: string;
  orders_closed: number;
  orders_cancelled: number;
  covers: number;
  average_spend: string;
  lines_voided: number;
  voided_value: string;
  post_close_orders: number;
  /**
   * The tax inside the day's takings, by rate — what a return is filed from.
   * Null when the site has no rates set. Absent on a Z signed before this was
   * added, which is why it is optional rather than nullable only.
   */
  tax?: DayTax | null;
  /** Sales in a class with no rate at this site, left out of `tax`. */
  untaxed?: { tax_class: string; gross: string }[];
  /**
   * What each promotion and coupon took off the day's settled bills. The
   * takings are already net of it. Absent on a Z signed before it was counted.
   */
  promotions?: DayPromotion[];
  warnings: string[];
}

export interface DayPromotion {
  /** As the bills named it. */
  name: string;
  /** A coupon's, rather than a promotion running on its own. */
  coupon: boolean;
  bills: number;
  amount: string;
}

export interface DayTaxBand {
  tax_class: string;
  label: string;
  percent: string;
  net: string;
  tax: string;
  gross: string;
}

export interface DayTax {
  bands: DayTaxBand[];
  net: string;
  tax: string;
  gross: string;
}

export interface BusinessDay {
  id: string;
  location_id: string;
  business_date: string;
  state: 'open' | 'closed';
  opened_at: string;
  closed_at: string | null;
  post_close_orders: number;
  z_report: DayReport | null;
  /** The site's own zone. A day is defined in it, so its times are read in it. */
  timezone: string;
}

export interface Receipt {
  id: string;
  order_id: string;
  series: string;
  number: number;
  reference: string;
  business_date: string;
  issued_at: string;
  reprint_count: number;
  payload: Record<string, unknown>;
}

export function useCurrentDay(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['fiscal', 'day', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<BusinessDay>(`/v1/fiscal/days/current?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 30_000,
  });
}

/**
 * One business day by its date: yesterday's Z report, or last month's.
 *
 * A date nothing was traded on answers 404, which is an answer rather than a
 * failure, so it is not retried.
 */
export function useDay(locationId: string | undefined, businessDate: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['fiscal', 'day', locationId ?? null, businessDate ?? null],
    queryFn: ({ signal }) =>
      api.get<BusinessDay>(`/v1/fiscal/days/${businessDate}?location_id=${locationId}`, signal),
    enabled: Boolean(locationId && businessDate),
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * The day so far.
 *
 * Never cached. Reading it twice and getting the same numbers would be a lie —
 * that is what the Z is for.
 */
export function useXReport(locationId: string | undefined, businessDate: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['fiscal', 'x-report', locationId ?? null, businessDate ?? null],
    queryFn: ({ signal }) =>
      api.get<DayReport>(
        `/v1/fiscal/days/${businessDate}/x-report?location_id=${locationId}`,
        signal,
      ),
    enabled: Boolean(locationId && businessDate),
    staleTime: 0,
  });
}

export function useCloseDay() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<BusinessDay, ApiError, { locationId: string; businessDate: string }>({
    mutationFn: ({ locationId, businessDate }) =>
      api.post<BusinessDay>(`/v1/fiscal/days/${businessDate}/close?location_id=${locationId}`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal'] });
    },
  });
}

export function useIssueReceipt() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Receipt, ApiError, { orderId: string }>({
    mutationFn: ({ orderId }) => api.post<Receipt>(`/v1/fiscal/receipts?order_id=${orderId}`),
    onSettled: () => {
      // A first issue creates a row and a reprint changes its count, so any
      // list showing receipts is now out of date either way.
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'receipts'] });
    },
  });
}

export interface ReceiptQuery {
  locationId: string | undefined;
  /** The receipt for one order: empty means printing it would mint a new number. */
  orderId?: string | undefined;
  /** What is printed on the paper, e.g. "R-000042". */
  reference?: string | undefined;
  businessDate?: string | undefined;
  limit?: number | undefined;
}

/**
 * Receipts at a site, highest number first.
 *
 * `location_id` is required because numbers run per site. Asking by `orderId`
 * is how a screen tells a reprint from a first issue before anything is
 * printed: one row back is a reprint, none means the next print mints a new
 * number — which is a fiscal document, and not something a manager browsing
 * yesterday's tabs should do without being told.
 */
export function useReceipts(query: ReceiptQuery) {
  const api = useApi();
  const search = new URLSearchParams();
  if (query.locationId) search.set('location_id', query.locationId);
  if (query.orderId) search.set('order_id', query.orderId);
  if (query.reference) search.set('reference', query.reference);
  if (query.businessDate) search.set('business_date', query.businessDate);
  if (query.limit) search.set('limit', String(query.limit));
  return useQuery({
    queryKey: [
      'fiscal',
      'receipts',
      query.locationId ?? null,
      query.orderId ?? null,
      query.reference ?? null,
      query.businessDate ?? null,
      query.limit ?? null,
    ],
    queryFn: ({ signal }) =>
      api.get<Receipt[]>(`/v1/fiscal/receipts?${search.toString()}`, signal),
    enabled: Boolean(query.locationId),
    staleTime: 10_000,
  });
}

/**
 * A VAT invoice.
 *
 * The same shape as a receipt, because it is the same row: both are numbered,
 * frozen documents in the `receipts` table, told apart by `series` — `R` for
 * what the guest takes, `INV` for what the company files. The invoice payload
 * carries `seller`, `buyer` and `tax` on top of everything a receipt has.
 */
export type Invoice = Receipt;

/** Who an invoice is addressed to. Typed in at the till; nothing derives it. */
export interface Buyer {
  name: string;
  address?: string | null;
  /** Optional: a sole trader or a private individual has none. */
  tax_number?: string | null;
}

/** The registered details a site invoices under. */
export interface TaxIdentity {
  location_id: string;
  /**
   * The registered company name, which is often not the name over the door:
   * the restaurant is "Acme Soho", the VAT is owed by "Acme Hospitality Ltd",
   * and it is the second one that belongs on an invoice.
   */
  legal_name: string;
  tax_number: string;
  address: string;
}

/**
 * Where a dated rate stands against the site's business day today:
 * `scheduled` starts later, `replaced` has been followed by a later one.
 */
export type TaxRateStatus = 'current' | 'scheduled' | 'replaced';

export interface TaxRate {
  /** Matches the `tax_class` on a menu item and an order line. */
  tax_class: string;
  percent: string;
  /** What to call it on the document. Null falls back to "Standard 20%". */
  label: string | null;
  /**
   * The first business day it applies to. Null for a rate with no start — a
   * class's first, which answers for everything sold before it too.
   */
  effective_from: string | null;
  status: TaxRateStatus;
}

/** A tax class that sells here with no rate to invoice it at. */
export interface UnratedClass {
  tax_class: string;
  /**
   * Dishes carrying it with no rate today, eaten in or taken away — including
   * dishes taken off the menu. Zero when only old bills carry it.
   */
  menu_items: number;
  /** The first business day it sold on with no rate in force, or null. */
  first_unrated_sale: string | null;
}

/**
 * A site's rates. Dated: a change is a new rate from a day, and everything
 * that bands tax — an invoice issued later, the X and Z for a past day — uses
 * the rate in force on the sale's own business day.
 */
export interface TaxRates {
  /** The site's business day today, which `rates` and each `status` are read against. */
  on: string;
  /** In force today, highest first. */
  rates: TaxRate[];
  /** Every rate the site has had or has scheduled, by class, newest first. */
  history: TaxRate[];
  /**
   * The server's answer to "what cannot be invoiced": every class a dish or
   * a bill carries with no rate to band it at. Not only the dishes on the
   * menu today — a dish taken off it, and a bill from before a class had a
   * rate, count too.
   */
  unrated_classes: UnratedClass[];
}

/**
 * What this site invoices under, or null if nobody has set it.
 *
 * Null is the ordinary state for a restaurant nobody has asked for an invoice,
 * and it is what a screen checks before offering to issue one: without it,
 * issuing refuses.
 */
export function useTaxIdentity(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['fiscal', 'tax-identity', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<TaxIdentity | null>(`/v1/fiscal/tax-identity?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

export function useSetTaxIdentity() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    TaxIdentity,
    ApiError,
    { locationId: string; legalName: string; taxNumber: string; address: string }
  >({
    mutationFn: (input) =>
      api.request<TaxIdentity>('/v1/fiscal/tax-identity', {
        method: 'PUT',
        body: {
          location_id: input.locationId,
          legal_name: input.legalName,
          tax_number: input.taxNumber,
          address: input.address,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'tax-identity'] });
    },
  });
}

/** A site's rates today, their dated history, and the classes with none. */
export function useTaxRates(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['fiscal', 'tax-rates', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<TaxRates>(`/v1/fiscal/tax-rates?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * Set what a tax class attracts here, from a day onwards.
 *
 * A change is a new dated rate: the old one keeps answering for everything
 * sold before the new one starts. `effectiveFrom` left out means no start for
 * a class's first rate, and today for a change to one that has a rate. The
 * same class and day twice replaces that day's rate.
 */
export function useSetTaxRate() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    TaxRate,
    ApiError,
    {
      locationId: string;
      taxClass: string;
      percent: string;
      label?: string | null;
      /** `YYYY-MM-DD`, a business day at the site. */
      effectiveFrom?: string | null;
    }
  >({
    mutationFn: (input) =>
      api.request<TaxRate>('/v1/fiscal/tax-rates', {
        method: 'PUT',
        body: {
          location_id: input.locationId,
          tax_class: input.taxClass,
          percent: input.percent,
          label: input.label ?? null,
          ...(input.effectiveFrom ? { effective_from: input.effectiveFrom } : {}),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'tax-rates'] });
    },
  });
}

/**
 * Take a rate out: with `effectiveFrom`, only the change starting that day
 * (a scheduled change that is not happening); without, every rate the class
 * has had — which leaves anything selling under it un-invoiceable.
 */
export function useRemoveTaxRate() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    ApiError,
    { locationId: string; taxClass: string; effectiveFrom?: string | null }
  >({
    mutationFn: ({ locationId, taxClass, effectiveFrom }) =>
      api.request<void>(
        `/v1/fiscal/tax-rates/${encodeURIComponent(taxClass)}?location_id=${locationId}` +
          (effectiveFrom ? `&effective_from=${effectiveFrom}` : ''),
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'tax-rates'] });
    },
  });
}

/**
 * Number and freeze a VAT invoice.
 *
 * Refused (409) without a tax identity for the site, and refused if anything
 * on the bill sells under a tax class with no rate — the response names the
 * class in `tax_class` so a screen can say which.
 *
 * Asking twice is a reprint of the same number, and `buyer` is left out for
 * one: a reprint returns the document as filed, and an invoice in somebody's
 * accounts does not change its mind about who it was addressed to. Omitting it
 * on a tab that has *not* been invoiced is refused (409, `field: "buyer"`)
 * rather than defaulted — an invoice addressed to nobody is not addressed.
 */
export function useIssueInvoice() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Invoice, ApiError, { orderId: string; buyer?: Buyer }>({
    mutationFn: ({ orderId, buyer }) =>
      api.post<Invoice>('/v1/fiscal/invoices', {
        order_id: orderId,
        ...(buyer ? { buyer } : {}),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'invoices'] });
    },
  });
}

/**
 * Invoices at a site, highest number first.
 *
 * Asking by `orderId` is how a screen tells a reprint from a first issue: one
 * row back means the tab has already been invoiced, and none means issuing
 * mints a new tax document.
 */
export function useInvoices(query: ReceiptQuery) {
  const api = useApi();
  const search = new URLSearchParams();
  if (query.locationId) search.set('location_id', query.locationId);
  if (query.orderId) search.set('order_id', query.orderId);
  if (query.reference) search.set('reference', query.reference);
  if (query.businessDate) search.set('business_date', query.businessDate);
  if (query.limit) search.set('limit', String(query.limit));
  return useQuery({
    queryKey: [
      'fiscal',
      'invoices',
      query.locationId ?? null,
      query.orderId ?? null,
      query.reference ?? null,
      query.businessDate ?? null,
      query.limit ?? null,
    ],
    queryFn: ({ signal }) =>
      api.get<Invoice[]>(`/v1/fiscal/invoices?${search.toString()}`, signal),
    enabled: Boolean(query.locationId),
    staleTime: 10_000,
  });
}

/** How a site's receipts look. See `useReceiptBranding`. */
export interface ReceiptBranding {
  location_id: string;
  display_name: string;
  header: string | null;
  footer: string | null;
  /** A `data:` URL, or null for no logo. */
  logo: string | null;
  /** The guest menu's colour, `#rrggbb`, or null for its default. */
  guest_accent: string | null;
  updated_at: string;
}

/**
 * A site's receipt branding, or null.
 *
 * Null when nothing is set, and on a plan without branded receipts whatever is
 * stored — the receipt prints plain rather than not at all. Fetched once and
 * kept: a receipt is printed many times an hour and the logo does not change.
 */
export function useReceiptBranding(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['fiscal', 'receipt-branding', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<ReceiptBranding | null>(
        `/v1/fiscal/receipt-branding?location_id=${locationId}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 5 * 60_000,
  });
}

export interface ReceiptBrandingInput {
  locationId: string;
  displayName: string;
  header: string;
  footer: string;
  /**
   * `keep` leaves the stored logo alone — what a form sends when only the
   * words changed. A data URL replaces it; null removes it.
   */
  logo: 'keep' | string | null;
  /**
   * The guest menu's colour as a hex colour, or null for its default. The
   * server refuses one words cannot be read on (`field: 'guest_accent'`).
   */
  guestAccent: string | null;
}

/** Replace a site's branding. 402 below Professional. */
export function useSetReceiptBranding() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ReceiptBranding, ApiError, ReceiptBrandingInput>({
    mutationFn: (input) =>
      api.request<ReceiptBranding>('/v1/fiscal/receipt-branding', {
        method: 'PUT',
        body: {
          location_id: input.locationId,
          display_name: input.displayName,
          header: input.header,
          footer: input.footer,
          guest_accent: input.guestAccent,
          ...(input.logo === 'keep' ? { keep_logo: true } : { logo: input.logo }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'receipt-branding'] });
    },
  });
}

export interface ReceiptEmailed {
  receipt_id: string;
  to_address: string;
  sent_at: string;
}

/**
 * Mail a receipt or invoice to a guest.
 *
 * 402 below Professional; 422 for something that is not an address; 502 when
 * the mail server would not take it (say "it did not go, print it"); 429 past
 * the restaurant's hourly allowance.
 */
export function useEmailReceipt() {
  const api = useApi();
  return useMutation<ReceiptEmailed, ApiError, { receiptId: string; to: string }>({
    mutationFn: ({ receiptId, to }) =>
      api.post<ReceiptEmailed>(`/v1/fiscal/receipts/${receiptId}/email`, { to }),
  });
}
